import React, { Fragment, useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import type { IClientUser } from '../../interfaces/user';
import Navigation from '../../components/Navigation';
import { GET_ADMIN_PROJECTS } from '../../graphql/GET_ADMIN_PROJECTS';
import searchIcon from '../../assets/svg/search.svg';

const AdminProjectsPage: React.FC<IClientUser> = (props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const projectsPerPage = 10;

  const { data, loading, error } = useQuery(GET_ADMIN_PROJECTS, {
    variables: {
      limit: 1000,
      page: 1
    }
  });

  const projects = data?.getAllProjectsAdmin?.projects || [];

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, statusFilter, sortBy]);

  const filterBySearch = (projectList: any[]) => {
    if (!searchTerm.trim()) return projectList;

    const lowerSearch = searchTerm.toLowerCase();
    return projectList.filter((project: any) => {
      const title = project.title?.toLowerCase() || '';
      const author = project.author?.displayName?.toLowerCase() || '';
      return title.includes(lowerSearch) || author.includes(lowerSearch);
    });
  };

  const filterByDate = (projectList: any[]) => {
    if (dateFilter === 'all') return projectList;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return projectList.filter((project: any) => {
      if (!project.createdAt) return false;
      const projectDate = new Date(project.createdAt);

      switch (dateFilter) {
        case 'today':
          return projectDate >= today;
        case 'last7days': {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return projectDate >= sevenDaysAgo;
        }
        case 'last30days': {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return projectDate >= thirtyDaysAgo;
        }
        default:
          return true;
      }
    });
  };

  const filterByStatus = (projectList: any[]) => {
    if (statusFilter === 'all') return projectList;

    switch (statusFilter) {
      case 'archived':
        return projectList.filter((project: any) => project.isArchived === true);
      case 'active':
        return projectList.filter((project: any) => project.isArchived === false);
      default:
        return projectList;
    }
  };

  const sortProjects = (projectList: any[]) => {
    const sorted = [...projectList];

    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        });
      case 'oldest':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateA - dateB;
        });
      case 'title-asc':
        return sorted.sort((a, b) => {
          const titleA = a.title?.toLowerCase() || '';
          const titleB = b.title?.toLowerCase() || '';
          return titleA.localeCompare(titleB);
        });
      case 'title-desc':
        return sorted.sort((a, b) => {
          const titleA = a.title?.toLowerCase() || '';
          const titleB = b.title?.toLowerCase() || '';
          return titleB.localeCompare(titleA);
        });
      default:
        return sorted;
    }
  };

  const filteredProjects = sortProjects(
    filterByStatus(filterByDate(filterBySearch(projects)))
  );

  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <main className="page-admin-projects">
      <Fragment>
        <Navigation {...props} />
        <section className="container">
          <div className="admin-users-container">
            <div className="admin-header">
              <div className="admin-header-content">
                <h1 className="admin-title">Projects</h1>
                <p className="admin-subtitle">View and manage all projects in the system</p>
              </div>
            </div>

          <div className="box">
            <div className="columns is-multiline">
              <div className="column is-12">
                <div className="field">
                  <div className="control has-icons-left">
                    <input
                      className="input"
                      type="text"
                      placeholder="Search by project title or author..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <span className="icon is-left">
                      <img src={searchIcon} alt="search" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="column is-4">
                <div className="field">
                  <label className="label">Date Filter</label>
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="last7days">Last 7 Days</option>
                        <option value="last30days">Last 30 Days</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="column is-4">
                <div className="field">
                  <label className="label">Status Filter</label>
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="active">Active</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="column is-4">
                <div className="field">
                  <label className="label">Sort By</label>
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="title-asc">Title (A-Z)</option>
                        <option value="title-desc">Title (Z-A)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <p className="has-text-grey">
                Showing {indexOfFirstProject + 1} to{' '}
                {Math.min(indexOfLastProject, filteredProjects.length)} of {filteredProjects.length}{' '}
                projects
              </p>
            </div>
          </div>

          {loading && (
            <div className="box has-text-centered">
              <p>Loading projects...</p>
            </div>
          )}

          {error && (
            <div className="notification is-danger">
              <p>Error loading projects: {error.message}</p>
            </div>
          )}

          {!loading && !error && currentProjects.length === 0 && (
            <div className="box has-text-centered">
              <p className="has-text-grey">No projects found.</p>
            </div>
          )}

          {!loading && !error && currentProjects.length > 0 && (
            <Fragment>
              <div className="box">
                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Author</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentProjects.map((project: any) => (
                        <tr key={project.id}>
                          <td>
                            <strong>{project.title || 'Untitled'}</strong>
                          </td>
                          <td>
                            {project.author?.profile?.name || 'Unknown'}
                            <br />
                            <small className="has-text-grey">{project.author?.email}</small>
                          </td>
                          <td>
                            <span className="tag is-info">{project.type || 'N/A'}</span>
                          </td>
                          <td>
                            {project.isArchived ? (
                              <span className="tag is-warning">Archived</span>
                            ) : (
                              <span className="tag is-success">Active</span>
                            )}
                          </td>
                          <td>{formatDate(project.createdAt)}</td>
                          <td>
                            <Link
                              to={`/project/${project.id}`}
                              className="button is-small is-link"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {totalPages > 1 && (
                <nav className="pagination is-centered" role="navigation">
                  <button
                    className="pagination-previous"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className="pagination-next"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                  <ul className="pagination-list">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <li key={page}>
                        <button
                          className={`pagination-link ${currentPage === page ? 'is-current' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}
            </Fragment>
          )}
          </div>
        </section>
      </Fragment>
    </main>
  );
};

export default AdminProjectsPage;
